import EquipmentStatus, {
  IEquipmentStatus,
  IEquipmentStatusApiModel,
} from '@models/EquipmentStatus.model';
import AppError from '@utils/AppError';
import EquipmentInventory, {
  IEquipmentInventory,
  IEquipmentInventoryApiModel,
} from './EquipmentInventory.model';
import Field, { IField, IFieldApiModel } from './Field.model';
import PictureEquipment, {
  IPictureEquipment,
  IPictureEquipmentApiModel,
} from './PictureEquipment.model';
import Product, {
  IProduct,
  IProductApiModel,
  ProductStatus,
} from './Product.model';
import Studio, { IStudio, IStudioApiModel } from './Studio.model';
import Tag, { ITag, ITagApiModel } from './Tag.model';
import User, { IUser, IUserApiModel } from './User.model';

export interface IPaginationResult<T> {
  total: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  results: T[];
}

export enum EquipmentIdentificationStatus {
  Identified = 'identified',
  Inventory = 'inventory',
  IdentificationRequested = 'identification_requested',
  Error = 'error',
  Discarded = 'discarded',
  Unidentified = 'unidentified',
}

export interface IEquipmentBase {
  id: string;
  purchasedPrice: number;
  purchasedFrom: string;
  purchasedDate: Date | null;
  serial: string;
  sla: string;
  productId: string | null;
  installedFirmwareVersion: string;
  installedSoftwareVersion: string;
  licenseStartDate: Date | null;
  licenseEndDate: Date | null;
  installDate: Date | null;
  vendor?: string;
  equipmentStatusId: string | null;
  equipmentStatus: IEquipmentStatus | undefined;
  identificationStatus: EquipmentIdentificationStatus | null;
  extendedWarrantyPurchased: null | boolean;
  extendedWarrantyLength: null | string;
  statusUser?: IUser;
  studioId: string | null;
  studio?: IStudio;
  userId: string | null;
  user: IUser | null;
  tags: ITag[];
  fields: IField[];
  equipmentsPictures: IPictureEquipment[];
  statusUpdatedAt?: Date;
  updatedAt: Date;
  // fields used by field configuration
  createdAt: Date;
  statusComment?: string;
  productName?: string;
  productModel?: string;
  productPage?: string;
  productDescription?: string;
  productCategory?: string | undefined;
  productWarranty?: number | undefined;
  productStatus?: ProductStatus | undefined;
  manufacturerName?: string;
  manufacturerImg: string | null;
  userName?: string;
  equipmentStatusName?: string;
  // importer fields
  importedModel: string | null;
  importedName: string | null;
  importedManufacturerName: string | null;
}

export interface IEquipment extends IEquipmentBase {
  product: IProduct;
}

export interface IEquipmentWithoutProduct extends IEquipmentBase {
  product?: IProduct;
}

export interface IEquipmentApiModel {
  id: string;
  purchasedPrice: number;
  purchasedFrom: string;
  purchasedDate: string | null;
  serial: string;
  sla: string;
  warranty: string;
  productId: string | null;
  licenseStartDate: string | null;
  licenseEndDate: string | null;
  installDate: string | null;
  vendor?: string;
  equipmentStatusId: string | null;
  equipmentStatus?: IEquipmentStatusApiModel;
  identificationStatus: EquipmentIdentificationStatus | null;
  extendedWarrantyPurchased: null | boolean;
  extendedWarrantyLength: null | string;
  installedFirmwareVersion: string;
  installedSoftwareVersion: string;
  statusUser?: IUserApiModel;
  product?: IProductApiModel;
  studioId: string | null;
  studio: IStudioApiModel | null;
  userId: string | null;
  statusComment?: string;
  user: IUserApiModel | null;
  tags: ITagApiModel[];
  fields: IFieldApiModel[];
  equipmentPictures: IPictureEquipmentApiModel[];
  statusUpdatedAt?: string;
  createdAt: string;
  updatedAt: string;
  // importer fields. May not be sent for equipments inventory
  importedModel: string | null;
  importedName: string | null;
  importedManufacturerName: string | null;
}

export default class Equipment implements IEquipment {
  public readonly id: string;

  public readonly purchasedPrice: number;

  public readonly purchasedFrom: string;

  public readonly purchasedDate: Date | null;

  public readonly serial: string;

  public readonly sla: string;

  public readonly warranty: Date | null;

  public readonly productId: string | null;

  public readonly vendor?: string;

  public readonly licenseStartDate: Date | null;

  public readonly licenseEndDate: Date | null;

  public readonly installDate: Date | null;

  public readonly equipmentStatusId: string | null;

  public readonly installedFirmwareVersion: string;

  public readonly installedSoftwareVersion: string;

  public readonly product: IProduct;

  public readonly equipmentStatus: IEquipmentStatus | undefined;

  public readonly identificationStatus: EquipmentIdentificationStatus | null;

  public readonly statusUser?: IUser;

  public readonly studioId: string | null;

  public readonly studio?: IStudio;

  public readonly userId: string | null;

  public readonly user: IUser | null;

  public readonly tags: ITag[];

  public readonly fields: IField[];

  public readonly equipmentsPictures: IPictureEquipment[];

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly productName: string | undefined;

  public readonly productModel: string | undefined;

  public readonly productPage: string | undefined;

  public readonly productDescription: string | undefined;

  public readonly manufacturerName: string | undefined;

  public readonly manufacturerImg: string | null;

  public readonly userName: string | undefined;

  public readonly statusUpdatedAt?: Date;

  public readonly statusComment?: string;

  public readonly equipmentStatusName?: string;

  public readonly extendedWarrantyPurchased: null | boolean;

  public readonly extendedWarrantyLength: null | string;

  public readonly importedModel: string | null;

  public readonly importedName: string | null;

  public readonly importedManufacturerName: string | null;

  public readonly productCategory?: string | undefined;

  public readonly productWarranty?: number | undefined;

  public readonly productStatus?: ProductStatus | undefined;

  constructor(
    id: string,
    purchasedPrice: number,
    purchasedFrom: string,
    purchasedDate: Date | null,
    serial: string,
    sla: string,
    warranty: Date | null,
    productId: string | null,
    product: IProduct,
    installedFirmwareVersion: string,
    installedSoftwareVersion: string,
    studioId: string | null,
    userId: string | null,
    user: IUser | null,
    tags: ITag[],
    fields: IField[],
    equipmentsPictures: IPictureEquipment[],
    createdAt: Date,
    updatedAt: Date,
    licenseStartDate: Date | null,
    licenseEndDate: Date | null,
    installDate: Date | null,
    extendedWarrantyPurchased: boolean | null,
    extendedWarrantyLength: string | null,
    equipmentStatusId: string | null,
    manufacturerImg: string | null,
    importedModel: string | null,
    importedName: string | null,
    importedManufacturerName: string | null,
    identificationStatus: EquipmentIdentificationStatus | null,

    productWarranty?: number | undefined,
    productStatus?: ProductStatus | undefined,
    studio?: IStudio,
    vendor?: string,
    equipmentStatus?: IEquipmentStatus,
    productName?: string,
    productModel?: string,
    manufacturerName?: string,
    userName?: string,
    equipmentStatusName?: string,
    productDescription?: string,
    statusComment?: string,
    statusUser?: IUser,
    statusUpdatedAt?: Date,
    productPage?: string,
    productCategory?: string | undefined,
  ) {
    this.id = id;
    this.purchasedPrice = purchasedPrice;
    this.purchasedFrom = purchasedFrom;
    this.purchasedDate = purchasedDate;
    this.serial = serial;
    this.sla = sla;
    this.warranty = warranty;
    this.productId = productId;
    this.vendor = vendor;
    this.licenseStartDate = licenseStartDate;
    this.licenseEndDate = licenseEndDate;
    this.installDate = installDate;
    this.product = product;
    this.equipmentStatusId = equipmentStatusId;
    this.equipmentStatus = equipmentStatus;
    this.studioId = studioId;
    this.installedFirmwareVersion = installedFirmwareVersion;
    this.installedSoftwareVersion = installedSoftwareVersion;
    this.studio = studio;
    this.userId = userId;
    this.user = user;
    this.tags = tags;
    this.fields = fields;
    this.equipmentsPictures = equipmentsPictures;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.productName = productName;
    this.productModel = productModel;
    this.productPage = productPage;
    this.manufacturerName = manufacturerName;
    this.manufacturerImg = manufacturerImg;
    this.identificationStatus = identificationStatus;
    this.userName = userName;
    this.equipmentStatusName = equipmentStatusName;
    this.productDescription = productDescription;
    this.statusComment = statusComment;
    this.statusUser = statusUser;
    this.statusUpdatedAt = statusUpdatedAt;
    this.extendedWarrantyPurchased = extendedWarrantyPurchased;
    this.extendedWarrantyLength = extendedWarrantyLength;
    this.productCategory = productCategory;
    this.productStatus = productStatus;
    this.productWarranty = productWarranty;
    this.importedModel = importedModel;
    this.importedName = importedName;
    this.importedManufacturerName = importedManufacturerName;
  }

  static fromApiModel(apiModel: IEquipmentApiModel) {
    const {
      id,
      purchasedPrice,
      purchasedFrom,
      serial,
      sla,
      productId,
      studioId,
      userId,
      installedFirmwareVersion,
      installedSoftwareVersion,
      equipmentStatusId,
      vendor,
      statusComment,
      extendedWarrantyPurchased,
      extendedWarrantyLength,
      importedModel,
      importedName,
      importedManufacturerName,
      identificationStatus,
    } = apiModel;

    const warranty = apiModel.warranty
      ? new Date(Date.parse(apiModel.warranty))
      : null;

    const statusUpdatedAt = apiModel.statusUpdatedAt
      ? new Date(Date.parse(apiModel.statusUpdatedAt))
      : undefined;

    const purchasedDate = apiModel.purchasedDate
      ? new Date(Date.parse(apiModel.purchasedDate))
      : null;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const licenseStartDate = apiModel.licenseStartDate
      ? new Date(Date.parse(apiModel.licenseStartDate))
      : null;
    const licenseEndDate = apiModel.licenseEndDate
      ? new Date(Date.parse(apiModel.licenseEndDate))
      : null;
    const installDate = apiModel.installDate
      ? new Date(Date.parse(apiModel.installDate))
      : null;

    const studio = apiModel.studio
      ? Studio.fromApiModel(apiModel.studio)
      : undefined;
    const user = apiModel.user ? User.fromApiModel(apiModel.user) : null;
    const statusUser = apiModel.statusUser
      ? User.fromApiModel(apiModel.statusUser)
      : undefined;
    const equipmentStatus = apiModel.equipmentStatus
      ? EquipmentStatus.fromApiModel(apiModel.equipmentStatus)
      : undefined;
    const equipmentStatusName = equipmentStatus
      ? equipmentStatus.name
      : undefined;
    const userName = user ? user.name : undefined;

    const tags = apiModel.tags ? Tag.arrayFromApiModel(apiModel.tags) : [];
    const fields = apiModel.fields
      ? Field.arrayFromApiModel(apiModel.fields)
      : [];
    const equipmentsPictures = apiModel.equipmentPictures
      ? PictureEquipment.arrayFromApiModel(apiModel.equipmentPictures)
      : [];

    if (!apiModel.product) {
      throw new AppError('Error parsing equipment, missing product');
    }
    const product = Product.fromApiModel(apiModel.product);

    let productName = product.name ? product.name : undefined;
    // Set by default if imported
    if (importedName && !productName) productName = importedName;
    let productModel = product.model ? product.model : undefined;
    // Set by default if imported
    if (importedModel && !productModel) productModel = importedModel;

    const productPage = product.productPage ? product.productPage : undefined;
    const productDescription = product.description
      ? product.description
      : undefined;
    let manufacturerName = product?.manufacturer?.name
      ? product.manufacturer.name
      : undefined;
    // Set by default if imported
    if (importedManufacturerName && !manufacturerName) {
      manufacturerName = importedManufacturerName;
    }

    const manufacturerImg = product?.manufacturer?.imageUrl
      ? product.manufacturer.imageUrl
      : null;
    const productCategory = product.category?.name;
    const { standardWarrantyLength: productWarranty, productStatus } = product;

    return new Equipment(
      id,
      purchasedPrice,
      purchasedFrom,
      purchasedDate,
      serial,
      sla,
      warranty,
      productId,
      product,
      installedFirmwareVersion,
      installedSoftwareVersion,
      studioId,
      userId,
      user,
      tags,
      fields,
      equipmentsPictures,
      createdAt,
      updatedAt,
      licenseStartDate,
      licenseEndDate,
      installDate,
      extendedWarrantyPurchased,
      extendedWarrantyLength,
      equipmentStatusId,
      manufacturerImg,
      importedModel,
      importedName,
      importedManufacturerName,
      identificationStatus,
      productWarranty ?? undefined,
      productStatus,
      studio,
      vendor,
      equipmentStatus,
      productName,
      productModel,
      manufacturerName,
      userName,
      equipmentStatusName,
      productDescription,
      statusComment,
      statusUser,
      statusUpdatedAt,
      productPage,
      productCategory,
    );
  }

  static fromApiModelWithoutProduct(apiModel: IEquipmentApiModel) {
    const {
      id,
      purchasedPrice,
      purchasedFrom,
      serial,
      sla,
      productId,
      studioId,
      userId,
      installedFirmwareVersion,
      installedSoftwareVersion,
      equipmentStatusId,
      vendor,
      extendedWarrantyLength,
      extendedWarrantyPurchased,
      importedModel,
      importedName,
      importedManufacturerName,
      identificationStatus,
    } = apiModel;

    const warranty = apiModel.warranty
      ? new Date(Date.parse(apiModel.warranty))
      : null;

    const purchasedDate = apiModel.purchasedDate
      ? new Date(Date.parse(apiModel.purchasedDate))
      : null;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const licenseStartDate = apiModel.licenseStartDate
      ? new Date(Date.parse(apiModel.licenseStartDate))
      : null;
    const licenseEndDate = apiModel.licenseEndDate
      ? new Date(Date.parse(apiModel.licenseEndDate))
      : null;
    const installDate = apiModel.installDate
      ? new Date(Date.parse(apiModel.installDate))
      : null;

    const studio = apiModel.studio
      ? Studio.fromApiModel(apiModel.studio)
      : undefined;
    const user = apiModel.user ? User.fromApiModel(apiModel.user) : null;
    const equipmentStatus = apiModel.equipmentStatus
      ? EquipmentStatus.fromApiModel(apiModel.equipmentStatus)
      : undefined;
    const equipmentStatusName = equipmentStatus
      ? equipmentStatus.name
      : undefined;
    const userName = user ? user.name : undefined;

    const tags = apiModel.tags ? Tag.arrayFromApiModel(apiModel.tags) : [];
    const fields = apiModel.fields
      ? Field.arrayFromApiModel(apiModel.fields)
      : [];
    const equipmentsPictures = apiModel.equipmentPictures
      ? PictureEquipment.arrayFromApiModel(apiModel.equipmentPictures)
      : [];
    const manufacturerImg = null;
    // Set by default if imported
    const productName = importedName;
    const productModel = importedModel;
    const manufacturerName = importedManufacturerName;
    return {
      id,
      purchasedPrice,
      purchasedFrom,
      purchasedDate,
      serial,
      sla,
      warranty,
      productId,
      installedFirmwareVersion,
      installedSoftwareVersion,
      extendedWarrantyPurchased,
      extendedWarrantyLength,
      studioId,
      userId,
      user,
      tags,
      fields,
      equipmentsPictures,
      createdAt,
      updatedAt,
      licenseStartDate,
      licenseEndDate,
      installDate,
      studio,
      vendor,
      equipmentStatusId,
      equipmentStatus,
      userName,
      equipmentStatusName,
      manufacturerImg,
      identificationStatus,
      importedModel,
      importedName,
      importedManufacturerName,
      productName,
      productModel,
      manufacturerName,
    } as IEquipmentWithoutProduct;
  }

  static arrayFromApiModel(arrayApiModel: IEquipmentApiModel[]) {
    const array = arrayApiModel.map(apiModel =>
      Equipment.fromApiModel(apiModel),
    );

    return array;
  }

  static arrayWithoutProductFromApiModel(arrayApiModel: IEquipmentApiModel[]) {
    const array = arrayApiModel.map(apiModel =>
      Equipment.fromApiModelWithoutProduct(apiModel),
    );

    return array;
  }

  // Todo: Reconcile equipmentInventory and Equipment models
  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<IEquipmentInventoryApiModel>,
  ): IPaginationResult<IEquipmentInventory> {
    return EquipmentInventory.fromArrayPaginatedApiModel(arrayApiModel);
  }
}
